/**
 * **Un 401 cierra la sesión que lo provocó, no la que haya puesta cuando llega.**
 *
 * Un 401 no es un error cualquiera en este repo: cierra la sesión y lleva al login. Y la decisión
 * se tomaba mirando lo que hubiera en `localStorage` **en el momento de la respuesta**, que no es
 * con lo que salió la petición. Entre una cosa y otra puede haber pasado un cambio de cuenta:
 * cerrar sesión y entrar con otra en la misma pestaña no cancela los XHR que estuvieran en vuelo,
 * así que el 401 de la cuenta anterior aterriza sobre la sesión nueva y la borra. Quien acaba de
 * entrar se queda fuera, sin saber por qué, y con sus propias peticiones a medias
 * (revisión CODEX del PR #1735).
 *
 * Las claves de vigencia no lo evitan y no pueden: el carril (`carrilPeticiones`) y
 * `accionDeLaSesion` descartan la **respuesta** —no se pinta, no se avisa, no se recarga—, pero
 * para entonces el cierre de sesión ya ha pasado dentro del manejador del 401, que es código común
 * y no sabe de carriles. Son dos cosas distintas: ellos deciden qué se escribe en la pantalla, y
 * esto decide si se echa a alguien.
 *
 * ## Por qué vive aquí y no en cada manejador
 *
 * Porque hay **tres** sitios que cierran la sesión por un 401, y los tres por el mismo motivo
 * —cada uno tiene su petición que no pasa por los demás—:
 *
 * - `DatabaseModel.sendRequest`, por donde va casi todo;
 * - `RhFestivo.leerBoletin`, que va por su cuenta porque es un multipart a mano;
 * - `usuarioActualizado`, que atiende el 401 de `Usuario.me()`.
 *
 * Repartida, la comprobación estaba en uno de los tres —el del boletín, que la necesitaba porque
 * la lectura del PDF tarda minutos— y faltaba justo en el que más peticiones mueve. Es la misma
 * decisión que ya se tomó con el sello de sesión del carril (#262) y con el de los diálogos
 * (`accionDeLaSesion`, #59): un solo vigilante, sin junturas donde equivocarse.
 *
 * ## El token, y el mismo que inyecta la cabecera
 *
 * `tokenDeLaSesion` lee exactamente lo que `sendRequest` pone en `Authorization`, así que «de qué
 * sesión es este 401» es literalmente «con qué token se pidió». No se usa `identidadSesion`, que
 * contesta a otra pregunta —de qué sesión es un DATO que se va a pintar, y lo hace sobre
 * `Utils.getToken()`—; aquí hace falta el token tal cual viajó en la cabecera.
 */

/**
 * El token con el que sale —o salió— una petición: lo que `sendRequest` pone en `Authorization`.
 * `null` si no hay sesión guardada, que también es una respuesta válida: una petición que salió sin
 * token no puede cerrar la sesión de quien haya entrado después.
 */
export function tokenDeLaSesion() {
    try {
        const session = JSON.parse(localStorage.getItem('session') || 'null');
        return (session && (session.token || session.access_token)) || null;
    } catch (e) {
        return null;
    }
}

/** La sesión era la de ahora y se ha cerrado. */
export const CERRADA = 'cerrada';

/** El 401 es de otra sesión: no se cierra nada. */
export const OTRA_SESION = 'otra-sesion';

/** No hay aplicación con la que cerrar (arranque a medias, o fuera del navegador). */
export const SIN_APLICACION = 'sin-aplicacion';

/**
 * Cierra la sesión por un 401 **solo si el 401 es de la sesión que hay ahora**.
 *
 * No toca el indicador de espera: cada manejador decide eso por su cuenta, porque no todos lo
 * encienden igual (ver `preloaderDeLaSesion` y la cuenta de `barreraRuta`). Lo que sí garantiza es
 * que nadie lo apague por un 401 ajeno.
 *
 * @param tokenEnviado el token con el que salió la petición (`tokenDeLaSesion()` al lanzarla)
 * @returns {string} `CERRADA`, `OTRA_SESION` o `SIN_APLICACION`
 */
export default function cerrarSesionPor401(tokenEnviado) {
    if (tokenDeLaSesion() !== tokenEnviado) return OTRA_SESION;
    const app = (typeof window != 'undefined' && window.app) || null;
    if (!app || !app.methods || typeof app.methods.removeSession != 'function') {
        return SIN_APLICACION;
    }
    // `removeSession` borra la sesión y el usuario, avisa por el `EventBus` y navega al login.
    app.methods.removeSession();
    return CERRADA;
}
